import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from "@angular/core";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { xor } from "lodash";
import { Subject } from "rxjs";
import { first, takeUntil } from "rxjs/operators";
import { Role } from "src/app/auth/models/role";
import { PhoneNumberRegexString } from "src/app/shared/regex/regexes";
import { User } from "../../../auth/models/user";
import { UserViewModel } from "../../../auth/models/user.viewmodel";
import { UserService } from "../../../auth/user.service";
import { UnlockUserDto } from "../../../shared/Models/write/UnlockUserDto";
import { ToastService } from "../../../shared/toast.service";

@Component({
  selector: "app-admin-edit-user",
  templateUrl: "./admin-edit-user.component.html",
  styleUrls: ["./admin-edit-user.component.scss"],
  providers: [ToastService],
})
export class AdminEditUserComponent implements OnInit, OnChanges, OnDestroy {
  addUserForm: FormGroup;
  userRole: Role[] = [];
  userSelected: User;
  selectedRole: string[] = [];
  selectedRoleIds: string[] = [];

  @Output() UserEdited: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() unlockUser: EventEmitter<string> = new EventEmitter<string>();
  // receive data from parent
  @Input() selectedUser;

  constructor(
    private _userService: UserService,
    private _toastService: ToastService
  ) {}
  destroy$ = new Subject();
  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }
  ngOnInit() {
    this._userService
      .getUserRoles()
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        this.userRole = res;
      });

    this.addUserForm = new FormGroup({
      id: new FormControl(""),
      firstNameInput: new FormControl("", [
        Validators.required,
        Validators.minLength(2),
      ]),
      lastNameInput: new FormControl("", [
        Validators.required,
        Validators.minLength(2),
      ]),
      mailInput: new FormControl("", [Validators.required, Validators.email]),
      // regex info -> https://regexr.com/3aevr
      telInput: new FormControl("", [
        Validators.required,
        Validators.pattern(PhoneNumberRegexString),
      ]),
      // 'telInput': new FormControl('', [Validators.required]),
      roleId: new FormControl(""),
      role: new FormControl(""),
      guid: new FormControl(""),
    });
  }

  updateForm(selectedUser: UserViewModel) {
    if (!this.addUserForm) return;

    this.addUserForm.controls.id.setValue(selectedUser.userId);
    this.addUserForm.controls.firstNameInput.setValue(selectedUser.firstName);
    this.addUserForm.controls.lastNameInput.setValue(selectedUser.lastName);
    this.addUserForm.controls.mailInput.setValue(selectedUser.email);
    this.addUserForm.controls.telInput.setValue(selectedUser.phoneNumber);
    this.addUserForm.controls.guid.setValue(selectedUser.userId);

    this.selectedRole = [];
    this.selectedRoleIds = [];

    selectedUser.roles.forEach((item) => {
      this.selectedRole.push(item.roleName);
      this.selectedRoleIds.push(item.roleId);
    });
  }

  dismiss(data: any) {
    var editUserModal = document.getElementById("editUserModal");

    if (data.target.id == "editUserModal" || data.target.id == "dismiss-btn") {
      editUserModal.style.display = "none";
      editUserModal.classList.remove("show");

      this.updateForm(this.selectedUser);

      this.selectedRole = [];
      this.selectedRoleIds = [];

      this.selectedUser.roles.forEach((item) => {
        this.selectedRole.push(item.roleName);
        this.selectedRoleIds.push(item.roleId);
      });
    }
  }

  updateUser() {
    var editUserModal = document.getElementById("editUserModal");

    if (this.addUserForm.valid)
      if (this.selectedRole.length == 0) {
        this._toastService.showErrorToast(
          "Error updating user",
          "please select userRole!"
        );
      } else {
        var id = this.addUserForm.get("id").value;
        var firstName = this.addUserForm.get("firstNameInput").value;
        var lastName = this.addUserForm.get("lastNameInput").value;
        var mail = this.addUserForm.get("mailInput").value;
        var telInput = this.addUserForm.get("telInput").value;

        var userToUpdate = new UserViewModel({
          id: id,
          mail: mail,
          firstName: firstName,
          lastName: lastName,
          tel: telInput,
          roleIds: this.selectedRoleIds,
        });

        this._userService
          .updateUser(userToUpdate)
          .pipe(first())
          .subscribe(
            (res) => {
              this._toastService.showSuccesToast(
                $localize`:@@admin.user.updated:User has been updated!`,
                $localize`:@@general.user:user` + ": " + firstName
              );

              this.UserEdited.emit(true);

              editUserModal.style.display = "none";
              editUserModal.classList.remove("show");
              this.selectedRole = [];
            },
            () => {}
          );
      }
    else
      this._toastService.showErrorToast(
        $localize`:@@validation.filloutform:Please fill out the form!`,
        $localize`:@@validation.entervalidinfo:Please enter valid information in the form!`
      );
  }

  getSelectedRole(Role: Role, event: Event) {
    const target = event.srcElement as HTMLInputElement;
    target.blur();

    this.selectedRole = xor(this.selectedRole, [Role.roleName]);
    this.selectedRoleIds = xor(this.selectedRoleIds, [Role.roleId]);
  }

  onUnlockUser() {
    const dto = new UnlockUserDto({
      userId: this.selectedUser.userId,
    });
    this._userService
      .unlockUser(dto)
      .pipe(first())
      .subscribe((res) => {
        const editUserModal = document.getElementById("editUserModal");
        this.unlockUser.emit(this.selectedUser.userId);
        editUserModal.style.display = "none";
        editUserModal.classList.remove("show");
      });
  }

  get form() {
    return this.addUserForm.controls;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.hasOwnProperty("selectedUser"))
      this.updateForm(this.selectedUser);
  }
}
